import React from 'react';
import { motion } from 'framer-motion';

/**
 * DashboardTile - Unified card component for the Admin Dashboard grids.
 *
 * Because Tailwind's JIT compiler only detects fully-formed class strings,
 * gradient/border classes are passed as complete Tailwind class strings.
 *
 * Variants:
 *  - 'plain'      Soft-border card
 *  - 'primary'    Highlighted quick action (primary border)
 *  - 'gradient'   Colored gradient card (compact, icon-above-title)
 *  - 'wide'       Wide gradient card (icon-left-of-title, used for hero rows)
 *
 * Example:
 *   <DashboardTile
 *     icon={Music}
 *     title="Manage Music"
 *     description="Add, edit, or delete tracks"
 *     onClick={() => navigate('/admin/music')}
 *     variant="plain"
 *     iconColor="text-primary"
 *     delay={0.4}
 *   />
 */
export const DashboardTile = ({
  icon: Icon,
  title,
  description,
  onClick,
  variant = 'plain',
  // Full Tailwind class strings so JIT can detect them:
  gradientClass = '',          // e.g. 'bg-gradient-to-br from-purple-500/30 to-pink-500/30'
  borderClass = '',            // e.g. 'border-2 border-purple-500/50 hover:border-purple-500'
  iconBgClass = 'bg-gradient-to-br from-primary to-accent', // wide variant only
  iconColor = 'text-primary',
  testId,
  delay = 0,
  colSpanClass = '',           // e.g. 'md:col-span-2'
  iconSize = 'w-10 h-10',
}) => {
  const isWide = variant === 'wide';

  let base = '';
  if (variant === 'plain') {
    base = 'bg-card border border-border/50 hover:border-primary/50';
  } else if (variant === 'primary') {
    base = 'bg-card border-2 border-primary/60 hover:border-primary';
  } else {
    // gradient + wide use externally-provided full class strings
    base = `${gradientClass} ${borderClass}`;
  }

  return (
    <motion.button
      id={testId}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onClick={onClick}
      data-testid={testId}
      className={`${base} ${colSpanClass} rounded-2xl p-6 transition-all text-left group scroll-mt-24 target:ring-4 target:ring-primary target:ring-offset-2 target:ring-offset-background`}
    >
      {isWide ? (
        <div className="flex items-center gap-6">
          <div className={`w-14 h-14 rounded-full ${iconBgClass} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
            {Icon && <Icon className="w-7 h-7 text-white" />}
          </div>
          <div>
            <h3 className="text-xl font-bold text-foreground mb-1">{title}</h3>
            {description && (
              <p className="text-muted-foreground text-sm">{description}</p>
            )}
          </div>
        </div>
      ) : (
        <>
          {Icon && (
            <Icon
              className={`${iconSize} ${iconColor} mb-4 group-hover:scale-110 transition-transform`}
            />
          )}
          <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </>
      )}
    </motion.button>
  );
};

export default DashboardTile;
